import { v4 as uuid } from 'uuid'
import { cloneDeep } from 'lodash'
import { htmlToSlate, slateToHtml } from '@slate-serializers/html'

import { accessors, orderedEntriesAccessorMap } from '@/views/Playbooks/playbook.helpers'
import { deserializeConfig, serializeConfig } from '@/components/richTextEditor/helpers'

export const rebuildChecklistFlatOrder = (checklist) => {
  const categorizedItemIds = new Set()

  if (checklist.categories) {
    for (const cat of Object.values(checklist.categories)) {
      for (const itemId of cat.order || []) {
        categorizedItemIds.add(itemId)
      }
    }
  }

  const uncategorizedIds = checklist.order.filter((id) => !categorizedItemIds.has(id))
  const categorizedIds = (checklist.category_order || []).flatMap(
    (catId) => checklist.categories?.[catId]?.order || []
  )

  checklist.order = [...uncategorizedIds, ...categorizedIds]
}

export const add = (state, payload, accessor) => {
  const newId = uuid()
  const isOrdered = orderedEntriesAccessorMap[accessor]
  const sectionData = state.playbook.body[accessor]
  const nestedCategory = payload.categoryId ? sectionData.entries[payload.categoryId] : null

  state.dirty = true

  if (nestedCategory?.entries) {
    // Nested categories (Dynamic Prompts, Post-Call) — entry lives inside the category
    nestedCategory.entries[newId] = payload.entry
    nestedCategory.order.push(newId)
  } else {
    // Flat entries (Checklist, Sticky Notes, Notifications)
    sectionData.entries[newId] = payload.entry

    if (isOrdered) {
      sectionData.order.push(newId)
    }

    // If a matching checklist category exists, add the entry to its order
    if (payload.categoryId) {
      const category = sectionData.categories?.[payload.categoryId]

      if (category) {
        if (!category.order) {
          category.order = []
        }
        category.order.push(newId)
      }
    }

    if (accessor === accessors.CHECKLIST && sectionData.categories) {
      rebuildChecklistFlatOrder(sectionData)
    }
  }
}

export const edit = (state, payload, accessor) => {
  const isNestedEntry = !!payload.categoryId

  state.dirty = true

  if (isNestedEntry) {
    state.playbook.body[accessor].entries[payload.categoryId].entries[payload.entryId] =
      payload.entry
  } else {
    state.playbook.body[accessor].entries[payload.entryId] = payload.entry
  }
}

export const remove = (state, prevState, payload, accessor) => {
  const isNestedEntry = !!payload.categoryId
  const isOrdered = orderedEntriesAccessorMap[accessor]

  state.dirty = true

  if (isNestedEntry) {
    delete state.playbook.body[accessor].entries[payload.categoryId].entries[payload.entryId]
    state.playbook.body[accessor].entries[payload.categoryId].order = prevState.playbook.body[
      accessor
    ].entries[payload.categoryId].order.filter((entryId) => entryId !== payload.entryId)
  } else {
    delete state.playbook.body[accessor].entries[payload]

    if (isOrdered) {
      state.playbook.body[accessor].order = prevState.playbook.body[accessor].order.filter(
        (entryId) => entryId !== payload
      )
    }

    if (accessor === accessors.CHECKLIST) {
      const sectionData = state.playbook.body[accessor]
      if (sectionData.categories) {
        for (const category of Object.values(sectionData.categories)) {
          if (category.order) {
            category.order = category.order.filter((id) => id !== payload)
          }
        }
      }
    }
  }
}

export const lock = (state, prevState, payload, accessor) => {
  const isNestedEntry = !!payload.categoryId

  state.dirty = true

  if (isNestedEntry) {
    state.playbook.body[accessor].entries[payload.categoryId].entries[payload.entryId].is_locked =
      !prevState.playbook.body[accessor].entries[payload.categoryId].entries[payload.entryId]
        .is_locked
  } else {
    state.playbook.body[accessor].entries[payload].is_locked =
      !prevState.playbook.body[accessor].entries[payload].is_locked
  }
}

const handleChecklistWin = (state, prevState, payload, accessor) => {
  if (!prevState.playbook.body[accessor].entries[payload].win === true) {
    state.playbook.body[accessor].entries[payload].win = true
  } else {
    delete state.playbook.body[accessor].entries[payload].win
  }
}

const handleNestedWin = (state, prevState, payload, accessor) => {
  const { categoryId, entryId } = payload
  if (!prevState.playbook.body[accessor].entries[categoryId].entries[entryId].win === true) {
    state.playbook.body[accessor].entries[categoryId].entries[entryId].win = true
  } else {
    delete state.playbook.body[accessor].entries[categoryId].entries[entryId].win
  }
}

export const win = (state, prevState, payload, accessor) => {
  state.dirty = true

  if (typeof payload === 'string') {
    handleChecklistWin(state, prevState, payload, accessor)
  } else {
    handleNestedWin(state, prevState, payload, accessor)
  }
}

export const reorder = (state, payload, accessor) => {
  const isNestedEntry = !!payload.categoryId

  state.dirty = true

  if (isNestedEntry) {
    state.playbook.body[accessor].entries[payload.categoryId].order = payload.order
  } else {
    state.playbook.body[accessor].order = payload
  }
}

export const reorderAndMove = (state, payload, accessor) => {
  state.dirty = true
  state.playbook.body[accessor].entries[payload.source.id].entries = payload.source.entries
  state.playbook.body[accessor].entries[payload.source.id].order = payload.source.order
  state.playbook.body[accessor].entries[payload.destination.id].entries =
    payload.destination.entries
  state.playbook.body[accessor].entries[payload.destination.id].order = payload.destination.order
}

export const normalizeDPValues = (values, initialHtmlValidation = false) => {
  const updatedValues = cloneDeep(values)

  if (values.display_duration_seconds) {
    const seconds = parseFloat(values.display_duration_seconds)
    const unit = updatedValues.display_duration_unit || 'seconds'
    const normalizedSeconds = unit === 'minutes' ? seconds * 60 : seconds

    updatedValues.display_duration_seconds = normalizedSeconds
  }

  // Slate does some conversion of the HTML which prevents it from being identical on first load
  // We need to initially do that conversion so it doesn't display as having changes immediately
  if (initialHtmlValidation && values.display?.html) {
    const { html, ...displayWithoutHtml } = updatedValues.display
    const convertToSlate = htmlToSlate(values.display.html, deserializeConfig)
    const convertBackToHtml = slateToHtml(convertToSlate, serializeConfig)
    const normalizedDisplay = { html: convertBackToHtml, ...displayWithoutHtml }

    updatedValues.display = normalizedDisplay
  }

  return updatedValues
}

export const convertDurationToSeconds = (entries) => {
  if (!entries) return entries

  Object.values(entries).forEach((section) => {
    if (section.entries) {
      Object.values(section.entries).forEach((entry) => {
        if (typeof entry.display_duration_seconds === 'string') {
          entry.display_duration_seconds = Number(entry.display_duration_seconds)
        }
        if (entry.display_duration_unit === 'minutes') {
          entry.display_duration_seconds = Math.round(entry.display_duration_seconds * 60)
        }
        delete entry.display_duration_unit
      })
    }
  })
  return entries
}

export const sanitizeBody = (body) => {
  const sanitizedBody = cloneDeep(body)

  // Convert duration to seconds and remove unit
  if (sanitizedBody.deck?.entries) {
    sanitizedBody.deck.entries = convertDurationToSeconds(sanitizedBody.deck.entries)
  }

  Object.keys(body).forEach((section) => {
    if (Object.keys(body[section].entries).length < 1) delete sanitizedBody[section]
  })

  return sanitizedBody
}

export const addChecklistVariations = (state, payload) => {
  const checklistVariations = {}
  const agentChecklists = payload.map((agentChecklist) => agentChecklist.items)
  const checklistItems = agentChecklists.flatMap((agentChecklists) => agentChecklists)
  const filteredItems = checklistItems.filter(
    (checklistItem) => checklistItem.item_type === 'variation'
  )
  filteredItems.forEach((item) => {
    checklistVariations[item.checklist_properties_uuid] =
      (checklistVariations[item.checklist_properties_uuid] || 0) + 1
  })

  state.checklistVariations = checklistVariations
}

export const addChecklistItemVariations = (state, payload, checklistItemId) => {
  const checklistItemVariations = []

  payload.forEach((agentChecklist) => {
    const agentName = `${agentChecklist.user.first_name} ${agentChecklist.user.last_name}`
    const checklistItems = agentChecklist.items
    const checklistItem = checklistItems.find(
      (checklistItem) => checklistItem.checklist_properties_uuid === checklistItemId
    )

    const variation = {
      user: agentName,
      user_id: agentChecklist.user.user_id,
      text: checklistItem.text,
      created_at: checklistItem.created_at,
      checklist_properties_uuid: checklistItem.checklist_properties_uuid,
    }

    checklistItemVariations.push(variation)
  })

  state.checklistItemVariations[checklistItemId] = checklistItemVariations
}

export const getAllCategoryIDs = (entries) => {
  const result = []
  const categories = Object.keys(entries)
  for (const categoryId of categories) {
    const category = entries[categoryId]
    result.push(...category.order)
  }
  return result
}

export const addChecklistCategory = (state, payload) => {
  const newId = uuid()
  const { checklist } = state.playbook.body

  state.dirty = true

  if (!checklist.categories) {
    checklist.categories = {}
  }
  if (!checklist.category_order) {
    checklist.category_order = []
  }

  checklist.categories[newId] = { name: payload.entry.name, order: [] }
  checklist.category_order.push(newId)
}

export const removeChecklistCategory = (state, prevState, payload) => {
  const { checklist } = state.playbook.body
  const categoryId = payload

  state.dirty = true

  delete checklist.categories[categoryId]
  checklist.category_order = prevState.playbook.body.checklist.category_order.filter(
    (id) => id !== categoryId
  )

  // Clean up if no categories remain
  if (Object.keys(checklist.categories).length === 0) {
    delete checklist.categories
    delete checklist.category_order
  }

  rebuildChecklistFlatOrder(checklist)
}

export const renameChecklistCategory = (state, payload) => {
  state.dirty = true
  state.playbook.body.checklist.categories[payload.categoryId].name = payload.name
}

export const reorderChecklistCategories = (state, payload) => {
  state.dirty = true
  state.playbook.body.checklist.category_order = payload
  rebuildChecklistFlatOrder(state.playbook.body.checklist)
}

export const moveChecklistItem = (state, payload) => {
  const { entryId, targetCategoryId, newOrder } = payload
  const { checklist } = state.playbook.body

  state.dirty = true

  // Remove item from all category order arrays
  if (checklist.categories) {
    for (const category of Object.values(checklist.categories)) {
      if (category.order) {
        category.order = category.order.filter((id) => id !== entryId)
      }
    }
  }

  // Add item to target category's order, preserving master order
  if (targetCategoryId && checklist.categories?.[targetCategoryId]) {
    const categoryItems = [...(checklist.categories[targetCategoryId].order || []), entryId]
    checklist.categories[targetCategoryId].order = newOrder.filter((id) =>
      categoryItems.includes(id)
    )
  }

  checklist.order = newOrder
}

// When doing Mass Playbook Update (for Dynamic Prompts) it's possible to have existing IDs in some other decklist on Playbook already.
// In that case backend validation breaks when having same IDs in decklists in different Dynamic Prompts.
// So here we are replacing IDs with new UUIDs in the decklist when running Mass Playbook Update.
export const updateDecklistIDs = (entry) => {
  if (!Array.isArray(entry?.display)) {
    return entry
  }

  const newDisplay = entry.display.map((block) => {
    if (block.type !== 'closing_loop' || !block.decklist) return block

    const oldOrder = block.decklist.order
    const newOrder = oldOrder.map(() => uuid())

    const newEntries = {}
    oldOrder.forEach((oldId, i) => {
      newEntries[newOrder[i]] = block.decklist.entries[oldId]
    })

    return {
      ...block,
      decklist: { ...block.decklist, entries: newEntries, order: newOrder },
    }
  })

  return { ...entry, display: newDisplay }
}
